import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bold, Italic, List, Link2, Heading2, X, BookMarked, Strikethrough, ChevronDown, ChevronRight } from "lucide-react";
import WikiSidebar from "./WikiSidebar";
import WikiHeader from "./WikiHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modal component for editing refs/templates
function EditModal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#a2a9b1]">
          <h3 className="font-bold text-lg text-[#202122]">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#eaecf0] rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-auto max-h-[60vh]">
          {children}
        </div>
      </div>
    </div>
  );
}

// Helper: Parse infobox parameters from wikitext
function parseInfoboxParams(inner) {
  const params = {};
  const lines = inner.split('|').slice(1); // Skip template name
  let currentKey = '';
  let currentValue = '';
  
  for (const line of lines) {
    if (line.includes('=')) {
      // Save previous param if exists
      if (currentKey) {
        params[currentKey.trim()] = currentValue.trim();
      }
      const eqIdx = line.indexOf('=');
      currentKey = line.substring(0, eqIdx).trim();
      currentValue = line.substring(eqIdx + 1);
    } else {
      currentValue += '|' + line;
    }
  }
  // Save last param
  if (currentKey) {
    params[currentKey.trim()] = currentValue.trim();
  }
  
  return params;
}

// Helper: Rebuild infobox wikitext from params
function rebuildInfobox(templateName, params) {
  let wikitext = `{{${templateName}`;
  for (const [key, value] of Object.entries(params)) {
    wikitext += `\n | ${key} = ${value}`;
  }
  wikitext += '\n}}';
  return wikitext;
}

// Helper: Format citation content for display
function formatCitation(content, idx) {
  // Parse cite web template if present
  const citeMatch = content.match(/\{\{cite\s+web\s*\|([^}]+)\}\}/i);
  if (citeMatch) {
    const params = {};
    citeMatch[1].split('|').forEach(part => {
      const eqIdx = part.indexOf('=');
      if (eqIdx > -1) {
        const key = part.substring(0, eqIdx).trim();
        const value = part.substring(eqIdx + 1).trim();
        params[key] = value;
      }
    });
    
    const title = params.title || 'Untitled';
    const website = params.website || params.work || '';
    const url = params.url || '#';
    const date = params.date || '';
    
    let formatted = `<a href="${url}" target="_blank" class="text-[#3366cc]">"${title}"</a>`;
    if (website) formatted += `. <em>${website}</em>`;
    if (date) formatted += `. ${date}`;
    formatted += '.';
    
    return formatted;
  }
  
  // Return raw content if not a cite template
  return content.length > 100 ? content.substring(0, 100) + '...' : content;
}

export default function WikiEdit() {
  const { title } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [content, setContent] = useState("");
  const [originalFetchedContent, setOriginalFetchedContent] = useState(""); // Store original for smart ref handling
  const [isNewPage, setIsNewPage] = useState(false);
  const [username, setUsername] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [editorMode, setEditorMode] = useState("visual");
  const [visualEditorModified, setVisualEditorModified] = useState(false); // Track if visual editor was edited
  const [editorKey, setEditorKey] = useState(0); // Key to force re-render of visual editor
  const fileInputRef = useRef(null);
  const visualEditorRef = useRef(null);
  const contentRef = useRef(""); // Store content without triggering re-render
  const visualEditorInitialized = useRef(false); // Track if visual editor has been initialized
  const currentPageTitle = useRef(null); // Track current page to know when to reinitialize
  
  // Modal states for editing refs/templates
  const [editingRef, setEditingRef] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [refContent, setRefContent] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  
  // Insert citation modal state
  const [showInsertCitation, setShowInsertCitation] = useState(false);
  const [insertCitationCode, setInsertCitationCode] = useState("");
  
  const editingNotes = searchParams.get('tab') === 'notes';
  const sectionIndex = searchParams.get('section') ? parseInt(searchParams.get('section')) : null;
  const initialMode = searchParams.get('mode') === 'source' ? 'source' : 'visual';

  useEffect(() => {
    checkAuth();
  }, [title, editingNotes, sectionIndex]);

  // Set initial editor mode from URL param (only on first load)
  const initialModeSet = useRef(false);
  useEffect(() => {
    if (!initialModeSet.current && initialMode) {
      setEditorMode(initialMode);
      initialModeSet.current = true;
    }
  }, [initialMode]);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
        if (editingNotes) {
          fetchNotes(token);
        } else {
          fetchPage(token);
        }
      } else {
        localStorage.removeItem("wiki_token");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // Clean any internal placeholders that may have been accidentally saved
  const sanitizeContent = (content) => {
    if (!content) return content;
    // Remove any internal placeholder markers that should never be in saved content
    // These indicate a previous save bug - we can't recover the original, so just remove them
    return content.replace(/__(?:PRESERVED|REF|SELFREF|TEMPLATE|EXTLINK|NOWIKI)_\d+__/g, '');
  };

  const [sectionHeading, setSectionHeading] = useState("");

  const fetchPage = async (token) => {
    try {
      if (sectionIndex !== null) {
        // Fetch only the specific section
        const response = await axios.get(`${API}/wiki/page/${encodeURIComponent(title)}/section/${sectionIndex}?token=${token}`);
        const rawContent = response.data.content || "";
        const sanitized = sanitizeContent(rawContent);
        setContent(sanitized);
        setOriginalFetchedContent(sanitized);
        setSectionHeading(response.data.heading || "");
        setVisualEditorModified(false);
        setIsNewPage(false);
      } else {
        const response = await axios.get(`${API}/wiki/page/${encodeURIComponent(title)}?token=${token}`);
        const rawContent = response.data.content || "";
        const sanitized = sanitizeContent(rawContent);
        setContent(sanitized);
        setOriginalFetchedContent(sanitized);
        setVisualEditorModified(false);
        setIsNewPage(false);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setIsNewPage(true);
        setContent("");
        setOriginalFetchedContent("");
        setVisualEditorModified(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async (token) => {
    try {
      const response = await axios.get(`${API}/wiki/notes/${encodeURIComponent(title)}?token=${token}`);
      const rawContent = response.data.content || "";
      const sanitized = sanitizeContent(rawContent);
      setContent(sanitized);
      setOriginalFetchedContent(sanitized); // Store for smart ref handling
      setVisualEditorModified(false); // Reset modification tracking
      setIsNewPage(!response.data.content);
    } catch (error) {
      setContent("");
      setOriginalFetchedContent("");
      setVisualEditorModified(false);
      setIsNewPage(true);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // WIKITEXT TO HTML CONVERSION (for Visual Editor)
  // ============================================
  const wikitextToHtml = useCallback((wikitext) => {
    if (!wikitext) return '<p data-empty="true">Start typing to add content...</p>';
    
    let html = wikitext;
    
    // Store special wiki markup that must be preserved exactly
    const preservedMarkup = [];
    
    // Preserve signatures (~~~~) and other special markup
    html = html.replace(/(~{3,5})/g, (match) => {
      const idx = preservedMarkup.length;
      preservedMarkup.push(match);
      return `__PRESERVED_${idx}__`;
    });
    
    // Preserve HTML comments
    html = html.replace(/<!--[\s\S]*?-->/g, (match) => {
      const idx = preservedMarkup.length;
      preservedMarkup.push(match);
      return `__PRESERVED_${idx}__`;
    });
    
    // Preserve nowiki tags
    html = html.replace(/<nowiki>([\s\S]*?)<\/nowiki>/gi, (match) => {
      const idx = preservedMarkup.length;
      preservedMarkup.push(match);
      return `__PRESERVED_${idx}__`;
    });
    
    // Preserve pre tags
    html = html.replace(/<pre>([\s\S]*?)<\/pre>/gi, (match) => {
      const idx = preservedMarkup.length;
      preservedMarkup.push(match);
      return `__PRESERVED_${idx}__`;
    });
    
    // Preserve code tags
    html = html.replace(/<code>([\s\S]*?)<\/code>/gi, (match) => {
      const idx = preservedMarkup.length;
      preservedMarkup.push(match);
      return `__PRESERVED_${idx}__`;
    });
    
    // Store refs and templates as data attributes to preserve them
    const refs = [];
    const templates = [];
    const externalLinks = [];
    const collapseBlocks = [];
    
    // FIRST: Extract external links before any other processing
    html = html.replace(/\[(https?:\/\/[^\s\]]+)\s+([^\]]+)\]/gi, (match, url, text) => {
      const idx = externalLinks.length;
      externalLinks.push({ url, text });
      return `__EXTLINK_${idx}__`;
    });
    
    // IMPORTANT: Extract self-closing refs FIRST, before full refs
    // This prevents <ref name="x"/> from being mistakenly matched as the start of a full ref
    html = html.replace(/<ref\s+name=["']?([^"'\s/>]+)["']?\s*\/>/gi, (match, name) => {
      const idx = refs.length;
      refs.push({ full: match, name, selfClosing: true, idx });
      return `__SELFREF_${idx}__`;
    });
    
    // Extract and replace full refs with placeholders
    html = html.replace(/<ref([^>]*)>([\s\S]*?)<\/ref>/gi, (match, attrs, content) => {
      const idx = refs.length;
      // Parse name attribute if present
      const nameMatch = attrs.match(/name=["']?([^"'\s]+)["']?/i);
      const name = nameMatch ? nameMatch[1] : null;
      refs.push({ full: match, attrs: attrs.trim(), content, name, idx });
      return `__REF_${idx}__`;
    });
    
    // Extract templates with special handling for infoboxes, reflist, and collapse
    html = html.replace(/\{\{((?:[^{}]|\{(?!\{)|\}(?!\}))*)\}\}/g, (match, inner) => {
      const idx = templates.length;
      const templateName = inner.split('|')[0].trim().toLowerCase();
      const templateNameOriginal = inner.split('|')[0].trim();
      templates.push({ full: match, name: templateNameOriginal, inner, idx });
      
      // COLLAPSE TOP: Start of collapsible section
      if (templateName === 'collapse top' || templateName === 'cot' || templateName === 'collapsible section begin') {
        // Extract title parameter
        const parts = inner.split('|');
        const title = parts[1]?.trim() || 'Collapsed content';
        const collapseIdx = collapseBlocks.length;
        collapseBlocks.push({ startIdx: idx, title, templateFull: match });
        // The outer box and header are non-editable, but the content inside is editable
        // Content div does NOT have contenteditable="false", making it editable
        // The collapse is expanded by default in edit mode so users can edit the content
        return `<div class="wiki-collapse-box wiki-collapse-edit" data-collapse-idx="${collapseIdx}" data-template-idx="${idx}">
          <div class="wiki-collapse-header" contenteditable="false" data-collapse-idx="${collapseIdx}">
            <span class="wiki-collapse-toggle">▼</span>
            <span class="wiki-collapse-title">${title}</span>
            <span class="wiki-collapse-hint">[editing]</span>
          </div>
          <div class="wiki-collapse-content wiki-collapse-content-editable" data-collapse-idx="${collapseIdx}" data-template-idx="${idx}">`;
      }
      
      // COLLAPSE BOTTOM: End of collapsible section
      if (templateName === 'collapse bottom' || templateName === 'cob' || templateName === 'collapsible section end') {
        return `</div></div>`;
      }
      
      // INFOBOX: Render as Wikipedia-style table with editable values
      if (templateName.startsWith('infobox')) {
        const params = parseInfoboxParams(inner);
        let tableHtml = `<table class="wiki-infobox" contenteditable="false" data-template-idx="${idx}">`;
        
        // Title row
        const infoboxTitle = params.name || params.title || templateNameOriginal.replace(/^infobox\s*/i, '');
        tableHtml += `<tr><th colspan="2" class="wiki-infobox-title">${infoboxTitle}</th></tr>`;
        
        // Parameter rows
        for (const [key, value] of Object.entries(params)) {
          if (key === 'name' || key === 'title' || !value.trim()) continue;
          
          // Format the label
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          
          // Format the value - convert wiki links
          let displayValue = value
            .replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '<a href="/wiki/$1" class="wiki-link">$2</a>')
            .replace(/\[\[([^\]]+)\]\]/g, '<a href="/wiki/$1" class="wiki-link">$1</a>');
          
          tableHtml += `<tr>
            <th class="wiki-infobox-label">${label}</th>
            <td class="wiki-infobox-value" contenteditable="true" data-param-key="${key}" data-template-idx="${idx}">${displayValue}</td>
          </tr>`;
        }
        
        tableHtml += '</table>';
        return tableHtml;
      }
      
      // Cite templates inside refs - just use placeholder
      if (templateName.startsWith('cite')) {
        return `__TEMPLATE_${idx}__`;
      }
      
      // REFLIST: Will be rendered after we have all refs
      if (templateName === 'reflist') {
        return `__REFLIST_${idx}__`;
      }
      
      // Generic template - show as pill
      return `<span class="wiki-template-pill" contenteditable="false" data-template-idx="${idx}" title="Template: ${templateNameOriginal}">{{${templateNameOriginal}}}</span>`;
    });
    
    // Convert headings (must be at line start)
    html = html.replace(/^======\s*(.+?)\s*======$/gm, '<h6>$1</h6>');
    html = html.replace(/^=====\s*(.+?)\s*=====$/gm, '<h5>$1</h5>');
    html = html.replace(/^====\s*(.+?)\s*====$/gm, '<h4>$1</h4>');
    html = html.replace(/^===\s*(.+?)\s*===$/gm, '<h3>$1</h3>');
    html = html.replace(/^==\s*(.+?)\s*==$/gm, '<h2>$1</h2>');
    
    // Convert bold and italic (wiki syntax)
    html = html.replace(/'''(.+?)'''/g, '<strong>$1</strong>');
    html = html.replace(/''(.+?)''/g, '<em>$1</em>');
    
    // Support HTML tags: <b>, <i>, <s>, <u>, <small>, <big>, <sub>, <sup>
    // These are already valid HTML, but ensure they're preserved and rendered correctly
    // The tags are already in the content, so we just need to make sure they're not stripped
    // Mark HTML formatting tags to distinguish from wiki syntax
    html = html.replace(/<b>([\s\S]*?)<\/b>/gi, '<strong class="html-bold">$1</strong>');
    html = html.replace(/<i>([\s\S]*?)<\/i>/gi, '<em class="html-italic">$1</em>');
    html = html.replace(/<s>([\s\S]*?)<\/s>/gi, '<s class="html-strike">$1</s>');
    html = html.replace(/<strike>([\s\S]*?)<\/strike>/gi, '<s class="html-strike">$1</s>');
    html = html.replace(/<del>([\s\S]*?)<\/del>/gi, '<del class="html-del">$1</del>');
    html = html.replace(/<u>([\s\S]*?)<\/u>/gi, '<u class="html-underline">$1</u>');
    html = html.replace(/<ins>([\s\S]*?)<\/ins>/gi, '<ins class="html-ins">$1</ins>');
    html = html.replace(/<small>([\s\S]*?)<\/small>/gi, '<small class="html-small">$1</small>');
    html = html.replace(/<big>([\s\S]*?)<\/big>/gi, '<span class="html-big" style="font-size: 1.2em;">$1</span>');
    html = html.replace(/<sub>([\s\S]*?)<\/sub>/gi, '<sub class="html-sub">$1</sub>');
    html = html.replace(/<sup>([\s\S]*?)<\/sup>/gi, '<sup class="html-sup">$1</sup>');
    html = html.replace(/<mark>([\s\S]*?)<\/mark>/gi, '<mark class="html-mark">$1</mark>');
    
    // Convert wiki links
    html = html.replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '<a href="/wiki/$1" class="wiki-link" data-wiki-target="$1">$2</a>');
    html = html.replace(/\[\[([^\]]+)\]\]/g, '<a href="/wiki/$1" class="wiki-link" data-wiki-target="$1">$1</a>');
    
    // Convert bullet lists
    const lines = html.split('\n');
    let inList = false;
    let listHtml = '';
    const processedLines = [];
    
    for (const line of lines) {
      if (line.startsWith('* ')) {
        if (!inList) {
          inList = true;
          listHtml = '<ul>';
        }
        listHtml += `<li>${line.substring(2)}</li>`;
      } else {
        if (inList) {
          listHtml += '</ul>';
          processedLines.push(listHtml);
          inList = false;
          listHtml = '';
        }
        processedLines.push(line);
      }
    }
    if (inList) {
      listHtml += '</ul>';
      processedLines.push(listHtml);
    }
    html = processedLines.join('\n');
    
    // Convert paragraphs while preserving multiple blank lines
    // First, mark multiple consecutive newlines with spacer markers
    // 3 newlines = 1 blank line between paragraphs (normal)
    // 4 newlines = 2 blank lines = extra spacing
    // 5+ newlines = even more spacing
    html = html.replace(/\n{5,}/g, '\n\n__WIKI_SPACING_TRIPLE__\n\n');
    html = html.replace(/\n{4}/g, '\n\n__WIKI_SPACING_DOUBLE__\n\n');
    html = html.replace(/\n{3}/g, '\n\n__WIKI_SPACING__\n\n');
    
    // Split by double newlines for paragraph breaks
    const paragraphs = html.split(/\n\n/);
    html = paragraphs.map((block, idx) => {
      block = block.trim();
      if (!block) {
        return '';
      }
      // Handle spacing markers
      if (block === '__WIKI_SPACING__') {
        return '<div class="wiki-spacing"></div>';
      }
      if (block === '__WIKI_SPACING_DOUBLE__') {
        return '<div class="wiki-spacing-double"></div>';
      }
      if (block === '__WIKI_SPACING_TRIPLE__') {
        return '<div class="wiki-spacing-triple"></div>';
      }
      if (block.match(/^<(h[1-6]|ul|ol|div|table|blockquote)/i)) return block;
      if (block.match(/^__(?:REF|SELFREF|EXTLINK|REFLIST|TEMPLATE)_\d+__$/)) return block;
      // Convert single newlines within the block to <br> tags
      return `<p>${block.replace(/\n/g, '<br>')}</p>`;
    }).filter(Boolean).join('\n');
    
    // Build a map of named refs for reference numbering
    const refNameToNum = {};
    let refCounter = 1;
    refs.forEach((ref, idx) => {
      if (!ref.selfClosing) {
        if (ref.name) {
          refNameToNum[ref.name] = refCounter;
        }
        ref.displayNum = refCounter;
        refCounter++;
      }
    });
    
    // Assign display numbers to self-closing refs
    refs.forEach((ref) => {
      if (ref.selfClosing && ref.name && refNameToNum[ref.name]) {
        ref.displayNum = refNameToNum[ref.name];
      }
    });
    
    // NOW restore all placeholders with actual HTML elements
    
    // Restore ref placeholders as clickable pills
    html = html.replace(/__REF_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      const ref = refs[i];
      const num = ref?.displayNum || (i + 1);
      return `<span class="wiki-ref-pill" contenteditable="false" data-ref-idx="${i}" title="Click to edit citation">[${num}]</span>`;
    });
    
    // Restore self-closing ref placeholders
    html = html.replace(/__SELFREF_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      const ref = refs[i];
      const num = ref?.displayNum || '↑';
      return `<span class="wiki-ref-pill wiki-ref-repeat" contenteditable="false" data-ref-idx="${i}" title="Repeated citation: ${ref?.name}">[${num}]</span>`;
    });
    
    // Restore external link placeholders
    html = html.replace(/__EXTLINK_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      const link = externalLinks[i];
      if (link) {
        return `<a href="${link.url}" class="external-link" target="_blank">${link.text}</a>`;
      }
      return '';
    });
    
    // Restore template placeholders (cite templates etc.)
    html = html.replace(/__TEMPLATE_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      const template = templates[i];
      if (template) {
        // Show cite templates as a pill that can be preserved
        const templateName = template.name || 'template';
        return `<span class="wiki-template-pill wiki-cite-template" contenteditable="false" data-template-idx="${i}" title="Citation: ${templateName}">📄 ${templateName}</span>`;
      }
      return '';
    });
    
    // Restore REFLIST as formatted references section
    html = html.replace(/__REFLIST_(\d+)__/g, (match, idx) => {
      // Build the actual references list - NO heading here since it comes from == References == in wikitext
      let reflistHtml = `<div class="wiki-reflist" contenteditable="false" data-template-idx="${idx}">`;
      reflistHtml += '<ol class="wiki-references">';
      
      // Only include non-self-closing refs
      const mainRefs = refs.filter(r => !r.selfClosing);
      mainRefs.forEach((ref) => {
        const formattedCitation = formatCitation(ref.content, ref.idx);
        reflistHtml += `<li id="cite-${ref.displayNum}" class="wiki-reference-item">
          <span class="wiki-ref-backlink">↑</span>
          <span class="wiki-ref-content">${formattedCitation}</span>
        </li>`;
      });
      
      reflistHtml += '</ol></div>';
      return reflistHtml;
    });
    
    // Restore preserved markup (signatures, comments, nowiki, etc.)
    html = html.replace(/__PRESERVED_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      const markup = preservedMarkup[i];
      // Display as non-editable pill to show it exists but prevent editing
      if (markup) {
        // Show different displays based on type
        if (markup.match(/^~{3,5}$/)) {
          return `<span class="wiki-preserved-pill wiki-signature" contenteditable="false" data-preserved-idx="${i}" title="Signature">~~~~</span>`;
        }
        if (markup.startsWith('<!--')) {
          return `<span class="wiki-preserved-pill wiki-comment" contenteditable="false" data-preserved-idx="${i}" title="HTML Comment"><!-- ... --></span>`;
        }
        if (markup.toLowerCase().startsWith('<nowiki>')) {
          return `<span class="wiki-preserved-pill wiki-nowiki" contenteditable="false" data-preserved-idx="${i}" title="NoWiki block">&lt;nowiki&gt;</span>`;
        }
        if (markup.toLowerCase().startsWith('<pre>')) {
          return `<pre class="wiki-pre" contenteditable="false" data-preserved-idx="${i}">${markup.replace(/<\/?pre>/gi, '')}</pre>`;
        }
        if (markup.toLowerCase().startsWith('<code>')) {
          return `<code class="wiki-code" contenteditable="false" data-preserved-idx="${i}">${markup.replace(/<\/?code>/gi, '')}</code>`;
        }
        return `<span class="wiki-preserved-pill" contenteditable="false" data-preserved-idx="${i}">${markup.substring(0, 20)}...</span>`;
      }
      return '';
    });
    
    // Store data for later retrieval
    if (typeof window !== 'undefined') {
      window.__wikiRefs = refs;
      window.__wikiTemplates = templates;
      window.__wikiExternalLinks = externalLinks;
      window.__wikiPreservedMarkup = preservedMarkup;
      window.__wikiCollapseBlocks = collapseBlocks;
    }
    
    return html;
  }, []);

  // ============================================
  // PASTE-SAFE WRAPPER: converts pasted wikitext to HTML
  // without destroying the existing global ref/template arrays.
  // Merges new items and offsets data-*-idx attributes.
  // ============================================
  const wikitextToHtmlForPaste = useCallback((pastedWikitext) => {
    // 1. Snapshot every global array the converter will overwrite
    const savedRefs = window.__wikiRefs || [];
    const savedTemplates = window.__wikiTemplates || [];
    const savedExtLinks = window.__wikiExternalLinks || [];
    const savedPreserved = window.__wikiPreservedMarkup || [];
    const savedCollapse = window.__wikiCollapseBlocks || [];

    // 2. Convert — this overwrites the globals with only the pasted items
    let html = wikitextToHtml(pastedWikitext);

    const pastedRefs = window.__wikiRefs || [];
    const pastedTemplates = window.__wikiTemplates || [];
    const pastedExtLinks = window.__wikiExternalLinks || [];
    const pastedPreserved = window.__wikiPreservedMarkup || [];
    const pastedCollapse = window.__wikiCollapseBlocks || [];

    // 3. Offset every data-*-idx in the generated HTML so indices
    //    don't collide with items already in the page.
    const refOff = savedRefs.length;
    const tmplOff = savedTemplates.length;
    const presOff = savedPreserved.length;

    if (refOff > 0) {
      html = html.replace(/data-ref-idx="(\d+)"/g, (_, i) =>
        `data-ref-idx="${parseInt(i) + refOff}"`);
    }
    if (tmplOff > 0) {
      html = html.replace(/data-template-idx="(\d+)"/g, (_, i) =>
        `data-template-idx="${parseInt(i) + tmplOff}"`);
    }
    if (presOff > 0) {
      html = html.replace(/data-preserved-idx="(\d+)"/g, (_, i) =>
        `data-preserved-idx="${parseInt(i) + presOff}"`);
    }

    // 4. Merge: original items stay at their original indices,
    //    pasted items are appended after them.
    window.__wikiRefs = [...savedRefs, ...pastedRefs];
    window.__wikiTemplates = [...savedTemplates, ...pastedTemplates];
    window.__wikiExternalLinks = [...savedExtLinks, ...pastedExtLinks];
    window.__wikiPreservedMarkup = [...savedPreserved, ...pastedPreserved];
    window.__wikiCollapseBlocks = [...savedCollapse, ...pastedCollapse];

    return html;
  }, [wikitextToHtml]);

  // ============================================
  // HTML TO WIKITEXT CONVERSION (for saving)
  // ============================================
  const htmlToWikitext = useCallback((html) => {
    if (!html || html.includes('data-empty="true"')) return '';
    
    const refs = window.__wikiRefs || [];
    const templates = window.__wikiTemplates || [];
    const externalLinks = window.__wikiExternalLinks || [];
    const preservedMarkup = window.__wikiPreservedMarkup || [];
    
    // First, extract any updated infobox values from the DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Update templates with edited infobox values
    doc.querySelectorAll('.wiki-infobox-value[data-param-key]').forEach(cell => {
      const templateIdx = parseInt(cell.dataset.templateIdx);
      const paramKey = cell.dataset.paramKey;
      const newValue = cell.textContent.trim();
      
      if (templates[templateIdx]) {
        const template = templates[templateIdx];
        // Parse and rebuild the template with updated value
        const params = parseInfoboxParams(template.inner);
        params[paramKey] = newValue;
        template.full = rebuildInfobox(template.name, params);
        template.inner = template.full.slice(2, -2); // Remove {{ and }}
      }
    });
    
    // Process the DOM tree
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }
      
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
      }
      
      const tag = node.tagName.toLowerCase();
      const children = Array.from(node.childNodes).map(processNode).join('');
      
      // Handle preserved markup pills - restore exact original
      if (node.classList?.contains('wiki-preserved-pill')) {
        const idx = parseInt(node.dataset.preservedIdx);
        if (preservedMarkup[idx]) {
          return preservedMarkup[idx];
        }
        return '';
      }
      
      // Handle preserved pre/code blocks
      if (node.classList?.contains('wiki-pre')) {
        const idx = parseInt(node.dataset.preservedIdx);
        if (preservedMarkup[idx]) {
          return preservedMarkup[idx];
        }
        return `<pre>${children}</pre>`;
      }
      
      if (node.classList?.contains('wiki-code')) {
        const idx = parseInt(node.dataset.preservedIdx);
        if (preservedMarkup[idx]) {
          return preservedMarkup[idx];
        }
        return `<code>${children}</code>`;
      }
      
      // Handle collapse boxes - restore original templates
      if (node.classList?.contains('wiki-collapse-box')) {
        const templateIdx = parseInt(node.dataset.templateIdx);
        if (templates[templateIdx]) {
          // Get the content inside the collapse
          const contentDiv = node.querySelector('.wiki-collapse-content');
          const innerContent = contentDiv ? Array.from(contentDiv.childNodes).map(processNode).join('') : '';
          return templates[templateIdx].full + innerContent + '{{collapse bottom}}';
        }
        return '';
      }
      
      // Skip collapse content divs (handled by parent)
      if (node.classList?.contains('wiki-collapse-content') || node.classList?.contains('wiki-collapse-header')) {
        return '';
      }
      
      // Handle ref pills - restore original wikitext
      if (node.classList?.contains('wiki-ref-pill')) {
        const idx = parseInt(node.dataset.refIdx);
        if (refs[idx]) {
          return refs[idx].full;
        }
        return '';
      }
      
      // Handle template pills - restore original wikitext
      if (node.classList?.contains('wiki-template-pill')) {
        const idx = parseInt(node.dataset.templateIdx);
        if (templates[idx]) {
          return templates[idx].full;
        }
        return '';
      }
      
      // Handle infobox table - restore updated wikitext
      if (node.classList?.contains('wiki-infobox')) {
        const idx = parseInt(node.dataset.templateIdx);
        if (templates[idx]) {
          return templates[idx].full;
        }
        return '';
      }
      
      // Handle reflist - restore original wikitext
      if (node.classList?.contains('wiki-reflist')) {
        const idx = parseInt(node.dataset.templateIdx);
        if (templates[idx]) {
          return templates[idx].full;
        }
        return '{{reflist}}';
      }
      
      switch (tag) {
        case 'strong':
          // Check if this was from HTML <b> tag
          if (node.classList?.contains('html-bold')) {
            return `<b>${children}</b>`;
          }
          return `'''${children}'''`;
        case 'b':
          return `<b>${children}</b>`;
        case 'em':
          // Check if this was from HTML <i> tag
          if (node.classList?.contains('html-italic')) {
            return `<i>${children}</i>`;
          }
          return `''${children}''`;
        case 'i':
          return `<i>${children}</i>`;
        case 's':
        case 'strike':
          return `<s>${children}</s>`;
        case 'del':
          return `<del>${children}</del>`;
        case 'u':
        case 'ins':
          return `<u>${children}</u>`;
        case 'small':
          return `<small>${children}</small>`;
        case 'sub':
          return `<sub>${children}</sub>`;
        case 'sup':
          return `<sup>${children}</sup>`;
        case 'mark':
          return `<mark>${children}</mark>`;
        case 'h2':
          return `\n== ${children} ==\n`;
        case 'h3':
          return `\n=== ${children} ===\n`;
        case 'h4':
          return `\n==== ${children} ====\n`;
        case 'h5':
          return `\n===== ${children} =====\n`;
        case 'h6':
          return `\n====== ${children} ======\n`;
        case 'a':
          if (node.classList?.contains('wiki-link')) {
            const target = node.dataset.wikiTarget || children;
            if (target === children) {
              return `[[${children}]]`;
            }
            return `[[${target}|${children}]]`;
          }
          if (node.classList?.contains('external-link')) {
            return `[${node.href} ${children}]`;
          }
          return children;
        case 'ul':
          return '\n' + children + '\n';
        case 'li':
          return `* ${children}\n`;
        case 'br':
          return '\n';
        case 'p':
          return children + '\n\n';
        case 'div':
          // Handle wiki-spacing divs to preserve multiple blank lines
          if (node.classList?.contains('wiki-spacing')) {
            return '\n'; // One extra blank line
          }
          if (node.classList?.contains('wiki-spacing-double')) {
            return '\n\n'; // Two extra blank lines
          }
          if (node.classList?.contains('wiki-spacing-triple')) {
            return '\n\n\n'; // Three extra blank lines
          }
          return children;
        case 'table':
          // Skip infobox tables as they're handled above
          if (node.classList?.contains('wiki-infobox')) return '';
          return children;
        default:
          return children;
      }
    };
    
    let wikitext = processNode(doc.body);
    
    // CRITICAL: Restore any remaining __PRESERVED_X__ placeholders that leaked through
    // This ensures wikicode is NEVER saved with placeholder markers
    wikitext = wikitext.replace(/__PRESERVED_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      if (preservedMarkup[i]) {
        return preservedMarkup[i];
      }
      return ''; // Remove orphaned placeholders
    });
    
    // Also restore any __REF_X__, __SELFREF_X__, __TEMPLATE_X__, __EXTLINK_X__ placeholders
    wikitext = wikitext.replace(/__REF_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      if (refs[i]) {
        return refs[i].full;
      }
      return '';
    });
    
    wikitext = wikitext.replace(/__SELFREF_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      if (refs[i]) {
        return refs[i].full;
      }
      return '';
    });
    
    wikitext = wikitext.replace(/__TEMPLATE_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      if (templates[i]) {
        return templates[i].full;
      }
      return '';
    });
    
    wikitext = wikitext.replace(/__EXTLINK_(\d+)__/g, (match, idx) => {
      const i = parseInt(idx);
      if (externalLinks[i]) {
        return `[${externalLinks[i].url} ${externalLinks[i].text}]`;
      }
      return '';
    });
    
    // Clean up excessive whitespace but preserve intentional line breaks
    // First normalize multiple spaces to single space (except at start of lines for indentation)
    wikitext = wikitext.replace(/  +/g, ' ');
    
    // Normalize line endings - remove trailing whitespace on each line
    wikitext = wikitext.split('\n').map(line => line.trimEnd()).join('\n');
    
    // Collapse 3+ consecutive newlines to 2 (max 1 blank line between paragraphs)
    wikitext = wikitext.replace(/\n{3,}/g, '\n\n');
    
    // Remove leading/trailing whitespace
    wikitext = wikitext.trim();
    
    return wikitext;
  }, []);

  // ============================================
  // VISUAL EDITOR INITIALIZATION (Uncontrolled Pattern)
  // ============================================
  // Initialize visual editor content when:
  // 1. Content is loaded for the first time
  // 2. Switching to a different page
  // 3. Switching to visual mode from source mode
  useEffect(() => {
    // Only initialize when in visual mode
    if (editorMode !== 'visual') return;
    
    // Check if we need to (re)initialize
    const needsInit = !visualEditorInitialized.current || 
                      currentPageTitle.current !== title;
    
    if (needsInit && visualEditorRef.current && content !== undefined) {
      // Convert wikitext to HTML and set it
      visualEditorRef.current.innerHTML = wikitextToHtml(content);
      visualEditorInitialized.current = true;
      currentPageTitle.current = title;
    }
  }, [editorMode, content, title, wikitextToHtml]);

  // Reset initialization flag when switching to source mode
  // so visual editor reinitializes when switching back
  useEffect(() => {
    if (editorMode === 'source') {
      visualEditorInitialized.current = false;
    }
  }, [editorMode]);

  // ============================================
  // SWITCH BETWEEN MODES
  // ============================================
  const switchToSource = () => {
    if (editorMode === 'visual' && visualEditorRef.current) {
      // Always convert from Visual HTML to wikitext to capture any changes
      const newWikitext = htmlToWikitext(visualEditorRef.current.innerHTML);
      setContent(newWikitext);
    }
    setEditorMode("source");
  };

  const switchToVisual = () => {
    // Reset the initialized flag so the visual editor will re-initialize
    // with the latest content from source mode
    visualEditorInitialized.current = false;
    setVisualEditorModified(false); // Reset modification tracking
    setEditorMode("visual");
  };

  // ============================================
  // VISUAL EDITOR TOOLBAR ACTIONS
  // ============================================
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    visualEditorRef.current?.focus();
  };

  const formatBold = () => execCommand('bold');
  const formatItalic = () => execCommand('italic');
  const formatStrikethrough = () => execCommand('strikethrough');
  
  const formatHeading = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const text = range.toString() || 'Heading';
    
    const h2 = document.createElement('h2');
    h2.textContent = text;
    
    range.deleteContents();
    range.insertNode(h2);
    
    selection.collapse(h2, 1);
    visualEditorRef.current?.focus();
  };

  const formatList = () => execCommand('insertUnorderedList');
  
  const insertLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    const pageName = prompt("Enter wiki page name to link to:", selectedText);
    
    if (pageName) {
      const displayText = selectedText || pageName;
      const link = document.createElement('a');
      link.href = `/wiki/${pageName}`;
      link.className = 'wiki-link';
      link.dataset.wikiTarget = pageName;
      link.textContent = displayText;
      
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);
      }
    }
    visualEditorRef.current?.focus();
  };

  // Insert citation - opens modal for pasting wikicode
  const openInsertCitation = () => {
    setInsertCitationCode("");
    setShowInsertCitation(true);
  };

  const handleInsertCitation = () => {
    const code = insertCitationCode.trim();
    if (!code) {
      toast.error("Please enter citation wikicode");
      return;
    }
    
    // Get current wikitext from visual editor
    let currentWikitext = content;
    if (visualEditorRef.current) {
      currentWikitext = htmlToWikitext(visualEditorRef.current.innerHTML);
    }
    
    // Try to insert at cursor position in the visual editor
    const selection = window.getSelection();
    let insertPosition = currentWikitext.length; // Default to end
    
    // Find a reasonable insertion point - after the current paragraph or at cursor
    if (selection && selection.rangeCount > 0 && visualEditorRef.current?.contains(selection.anchorNode)) {
      // Insert at cursor - we'll append to current position in wikitext
      // For simplicity, insert before the reflist if it exists, otherwise at end
      const reflistIdx = currentWikitext.indexOf('{{reflist}}');
      if (reflistIdx > -1) {
        insertPosition = reflistIdx;
      }
    }
    
    // Insert the code
    const newWikitext = currentWikitext.slice(0, insertPosition) + code + currentWikitext.slice(insertPosition);
    
    // Update content and re-render visual editor
    setContent(newWikitext);
    
    // Close modal
    setShowInsertCitation(false);
    setInsertCitationCode("");
    
    toast.success("Citation inserted!");
  };

  // ============================================
  // HANDLE CLICKS ON REFS/TEMPLATES
  // ============================================
  const handleVisualEditorClick = (e) => {
    const target = e.target;
    
    // Handle ref pill click
    if (target.classList?.contains('wiki-ref-pill')) {
      e.preventDefault();
      e.stopPropagation();
      const idx = parseInt(target.dataset.refIdx);
      const refs = window.__wikiRefs || [];
      if (refs[idx]) {
        setEditingRef({ idx, ...refs[idx] });
        setRefContent(refs[idx].content || '');
      }
      return;
    }
    
    // Handle collapse header click - toggle visibility
    if (target.closest('.wiki-collapse-header')) {
      e.preventDefault();
      e.stopPropagation();
      const header = target.closest('.wiki-collapse-header');
      const collapseIdx = header.dataset.collapseIdx;
      const content = document.querySelector(`.wiki-collapse-content[data-collapse-idx="${collapseIdx}"]`);
      const hint = header.querySelector('.wiki-collapse-hint');
      
      if (content) {
        const isExpanded = content.style.display !== 'none';
        content.style.display = isExpanded ? 'none' : 'block';
        header.classList.toggle('expanded', !isExpanded);
        if (hint) {
          hint.textContent = isExpanded ? '[show]' : '[hide]';
        }
      }
      return;
    }
    
    // Handle template pill click (but not infobox or reflist or collapse)
    if (target.closest('.wiki-template-pill') && !target.closest('.wiki-infobox') && !target.closest('.wiki-reflist') && !target.closest('.wiki-collapse-box')) {
      e.preventDefault();
      e.stopPropagation();
      const pill = target.closest('.wiki-template-pill');
      const idx = parseInt(pill.dataset.templateIdx);
      const templates = window.__wikiTemplates || [];
      if (templates[idx]) {
        setEditingTemplate({ idx, ...templates[idx] });
        setTemplateContent(templates[idx].full);
      }
      return;
    }
  };

  // ============================================
  // SAVE REF/TEMPLATE EDITS
  // ============================================
  const saveRefEdit = () => {
    if (editingRef === null) return;
    
    const refs = window.__wikiRefs || [];
    const idx = editingRef.idx;
    
    if (editingRef.selfClosing) {
      toast.info("Self-closing refs reference another citation");
    } else {
      const attrs = refs[idx].attrs || '';
      refs[idx].full = `<ref${attrs ? ' ' + attrs : ''}>${refContent}</ref>`;
      refs[idx].content = refContent;
      window.__wikiRefs = refs;
      
      if (visualEditorRef.current) {
        const currentHtml = visualEditorRef.current.innerHTML;
        const newWikitext = htmlToWikitext(currentHtml);
        setContent(newWikitext);
      }
    }
    
    setEditingRef(null);
    setRefContent('');
  };

  const saveTemplateEdit = () => {
    if (editingTemplate === null) return;
    
    const templates = window.__wikiTemplates || [];
    const idx = editingTemplate.idx;
    
    templates[idx].full = templateContent;
    window.__wikiTemplates = templates;
    
    if (visualEditorRef.current) {
      const currentHtml = visualEditorRef.current.innerHTML;
      const newWikitext = htmlToWikitext(currentHtml);
      setContent(newWikitext);
    }
    
    setEditingTemplate(null);
    setTemplateContent('');
  };

  // ============================================
  // HANDLE KEYDOWN IN VISUAL EDITOR
  // ============================================
  const handleVisualEditorKeyDown = useCallback((e) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    // ============================================
    // HANDLE ENTER KEY - Insert single line break
    // ============================================
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      
      // Delete any selected content first
      if (!range.collapsed) {
        range.deleteContents();
      }
      
      // Insert a <br> element for single line break
      const br = document.createElement('br');
      range.insertNode(br);
      
      // Move cursor after the <br>
      const newRange = document.createRange();
      newRange.setStartAfter(br);
      newRange.setEndAfter(br);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      setVisualEditorModified(true);
      return;
    }
    
    // ============================================
    // HANDLE BACKSPACE at start of paragraph
    // ============================================
    if (e.key === 'Backspace') {
      // Only handle if cursor is collapsed (no selection)
      if (!range.collapsed) return;
      
      const container = range.startContainer;
      const offset = range.startOffset;
      
      // Helper: Find the containing block element
      const findContainingBlock = (node) => {
        while (node && node !== visualEditorRef.current) {
          if (node.nodeType === Node.ELEMENT_NODE && 
              ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI'].includes(node.tagName)) {
            return node;
          }
          node = node.parentElement;
        }
        return null;
      };
      
      // Helper: Check if we're at the absolute start of a block
      const isAtBlockStart = (block, node, nodeOffset) => {
        // Get all text content before cursor position within this block
        const treeWalker = document.createTreeWalker(
          block,
          NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
          null,
          false
        );
        
        let textBeforeCursor = '';
        let currentNode;
        
        while ((currentNode = treeWalker.nextNode())) {
          if (currentNode === node) {
            // We've reached the cursor node
            if (currentNode.nodeType === Node.TEXT_NODE) {
              textBeforeCursor += currentNode.textContent.slice(0, nodeOffset);
            }
            break;
          }
          
          if (currentNode.nodeType === Node.TEXT_NODE) {
            textBeforeCursor += currentNode.textContent;
          }
        }
        
        // We're at the start if there's no visible text before cursor
        return textBeforeCursor.trim() === '';
      };
      
      const currentBlock = findContainingBlock(container);
      
      if (currentBlock && isAtBlockStart(currentBlock, container, offset)) {
        // Find previous sibling block (skip <br> elements)
        let prevBlock = currentBlock.previousElementSibling;
        while (prevBlock && prevBlock.tagName === 'BR') {
          prevBlock = prevBlock.previousElementSibling;
        }
        
        // Also check for previous sibling that's a text node or inline element
        let prevSibling = currentBlock.previousSibling;
        while (prevSibling && prevSibling.nodeType === Node.TEXT_NODE && prevSibling.textContent.trim() === '') {
          prevSibling = prevSibling.previousSibling;
        }
        
        if (prevBlock && ['P', 'DIV'].includes(prevBlock.tagName) && !prevBlock.classList.contains('infobox')) {
          e.preventDefault();
          e.stopPropagation();
          
          // Find the last text position in previous block
          let lastTextNode = null;
          let lastOffset = 0;
          
          const findLastTextPosition = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
              lastTextNode = node;
              lastOffset = node.length;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
              for (let i = node.childNodes.length - 1; i >= 0; i--) {
                findLastTextPosition(node.childNodes[i]);
                if (lastTextNode) break;
              }
            }
          };
          
          findLastTextPosition(prevBlock);
          
          // If previous block ends with a citation (sup element), handle specially
          const lastChild = prevBlock.lastElementChild;
          if (lastChild && lastChild.tagName === 'SUP') {
            // Add a space after the citation before merging
            const space = document.createTextNode(' ');
            prevBlock.appendChild(space);
            lastTextNode = space;
            lastOffset = 1;
          } else if (!lastTextNode) {
            // No text in previous block, create one
            const textNode = document.createTextNode('');
            prevBlock.appendChild(textNode);
            lastTextNode = textNode;
            lastOffset = 0;
          }
          
          // Move all children from current block to previous block
          while (currentBlock.firstChild) {
            prevBlock.appendChild(currentBlock.firstChild);
          }
          
          // Remove the now-empty current block
          currentBlock.remove();
          
          // Set cursor position
          if (lastTextNode) {
            const newRange = document.createRange();
            try {
              newRange.setStart(lastTextNode, lastOffset);
              newRange.setEnd(lastTextNode, lastOffset);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } catch (err) {
              console.log('Error setting cursor:', err);
            }
          }
          
          setVisualEditorModified(true);
        }
      }
    }
  }, []);

  // ============================================
  // HANDLE SELECTION CHANGE FOR CITATION HIGHLIGHTING
  // ============================================
  const handleSelectionChange = useCallback(() => {
    if (!visualEditorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    // Remove previous highlights from all pill types
    visualEditorRef.current.querySelectorAll('.selected').forEach(el => {
      el.classList.remove('selected');
    });
    
    // If there's a selection (not collapsed)
    if (!range.collapsed && visualEditorRef.current.contains(range.commonAncestorContainer)) {
      // Highlight citation pills (wiki-ref-pill) that are within the selection
      visualEditorRef.current.querySelectorAll('.wiki-ref-pill').forEach(pill => {
        if (selection.containsNode(pill, true)) {
          pill.classList.add('selected');
        }
      });
      
      // Also highlight template pills that are within the selection
      visualEditorRef.current.querySelectorAll('.wiki-template-pill').forEach(pill => {
        if (selection.containsNode(pill, true)) {
          pill.classList.add('selected');
        }
      });
      
      // Also highlight preserved pills (signatures, comments, etc.)
      visualEditorRef.current.querySelectorAll('.wiki-preserved-pill').forEach(pill => {
        if (selection.containsNode(pill, true)) {
          pill.classList.add('selected');
        }
      });
    }
  }, []);

  // Set up selection change listener
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // ============================================
  // HANDLE INPUT IN VISUAL EDITOR (Without triggering re-render)
  // ============================================
  const handleVisualEditorInput = useCallback(() => {
    // Mark as modified using ref instead of state to avoid re-render
    // The actual state will be updated only when needed (e.g., switching modes, saving)
    if (!visualEditorModified) {
      setVisualEditorModified(true);
    }
  }, [visualEditorModified]);

  // ============================================
  // HANDLE PASTE IN VISUAL EDITOR
  // ============================================
  const handleVisualEditorPaste = useCallback((e) => {
    // Get pasted text from clipboard
    let pastedText = '';
    
    // Try to get clipboard data
    if (e.clipboardData) {
      pastedText = e.clipboardData.getData('text/plain') || e.clipboardData.getData('text') || '';
    } else if (window.clipboardData) {
      // IE fallback
      pastedText = window.clipboardData.getData('Text') || '';
    }
    
    console.log('Paste detected, content:', pastedText.substring(0, 100));
    
    if (!pastedText) {
      console.log('No text in clipboard');
      return; // Let browser handle it
    }
    
    // Check if pasted content contains wiki markup (refs, templates, links)
    const hasWikiMarkup = /<ref|<\/ref>|\{\{|\}\}|\[\[|\]\]/i.test(pastedText);
    
    console.log('Has wiki markup:', hasWikiMarkup);
    
    if (hasWikiMarkup) {
      e.preventDefault();
      e.stopPropagation();
      
      // Normalize line breaks in pasted content
      pastedText = pastedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      // Get current selection
      const selection = window.getSelection();
      
      if (selection && selection.rangeCount > 0 && visualEditorRef.current) {
        const range = selection.getRangeAt(0);
        
        if (visualEditorRef.current.contains(range.commonAncestorContainer)) {
          // Delete any selected content first
          if (!range.collapsed) {
            range.deleteContents();
          }
          
          // Convert the pasted wikitext to HTML (merge-safe, preserves existing refs)
          const pastedHtml = wikitextToHtmlForPaste(pastedText);
          
          // Create a temporary container to hold the HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = pastedHtml;
          
          // Insert each child node at the cursor position
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          
          // Insert the fragment
          range.insertNode(fragment);
          
          // Move cursor to after the inserted content
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Mark as modified
          setVisualEditorModified(true);
          
          // Update the content state from the modified visual editor (for save)
          // But do NOT trigger a re-render that would reset cursor
          const newWikitext = htmlToWikitext(visualEditorRef.current.innerHTML);
          // Use a ref to store content without triggering re-render
          contentRef.current = newWikitext;
          
          toast.success("Wiki markup pasted!");
          return;
        }
      }
      
      // Fallback: if we couldn't insert directly, append to the visual editor DOM
      if (visualEditorRef.current) {
        // Get current content
        let currentHtml = visualEditorRef.current.innerHTML;
        
        // Clean up placeholder text
        if (currentHtml.includes('Start typing to add content...')) {
          currentHtml = currentHtml.replace('Start typing to add content...', '');
        }
        
        // Convert pasted wikitext to HTML (merge-safe, preserves existing refs)
        const pastedHtml = wikitextToHtmlForPaste(pastedText);
        
        // Find reflist and insert before it, or append at end
        const reflistMatch = currentHtml.match(/<div[^>]*class="reflist"[^>]*>[\s\S]*?<\/div>/i);
        if (reflistMatch) {
          const reflistIndex = currentHtml.indexOf(reflistMatch[0]);
          visualEditorRef.current.innerHTML = 
            currentHtml.slice(0, reflistIndex) + 
            '<p>' + pastedHtml + '</p>' + 
            currentHtml.slice(reflistIndex);
        } else {
          visualEditorRef.current.innerHTML = currentHtml + '<p>' + pastedHtml + '</p>';
        }
        
        // Move cursor to end
        const range = document.createRange();
        range.selectNodeContents(visualEditorRef.current);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        
        setVisualEditorModified(true);
        toast.success("Wiki markup pasted!");
        return;
      }
    }
    
    // For non-wiki content, let the browser handle normal paste
    // But still normalize line breaks
    if (pastedText.includes('\n')) {
      e.preventDefault();
      
      // Normalize line breaks
      const normalizedText = pastedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      // Insert as text nodes with br elements for line breaks
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        if (!range.collapsed) {
          range.deleteContents();
        }
        
        // Split by newlines and insert with proper line breaks
        const lines = normalizedText.split('\n');
        const fragment = document.createDocumentFragment();
        
        lines.forEach((line, idx) => {
          if (line) {
            fragment.appendChild(document.createTextNode(line));
          }
          if (idx < lines.length - 1) {
            fragment.appendChild(document.createElement('br'));
          }
        });
        
        range.insertNode(fragment);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        
        setVisualEditorModified(true);
      }
    }
  }, [content, htmlToWikitext, wikitextToHtmlForPaste]);

  // ============================================
  // HANDLE COPY IN VISUAL EDITOR
  // ============================================
  const handleVisualEditorCopy = useCallback((e) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    if (range.collapsed) return; // Nothing selected
    
    // Check if selection is within the visual editor
    if (!visualEditorRef.current?.contains(range.commonAncestorContainer)) return;
    
    // Get the selected HTML
    const fragment = range.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);
    const selectedHtml = tempDiv.innerHTML;
    
    // Convert the selected HTML to wikitext
    const selectedWikitext = htmlToWikitext(selectedHtml);
    
    // Only intercept if the selection contains wiki markup (refs, templates, etc.)
    if (selectedWikitext.includes('<ref') || selectedWikitext.includes('{{') || selectedWikitext.includes('[[')) {
      e.preventDefault();
      
      // Set the clipboard data with the wikitext
      if (e.clipboardData) {
        e.clipboardData.setData('text/plain', selectedWikitext);
        e.clipboardData.setData('text/html', selectedHtml);
      }
      
      console.log('Copied wikitext:', selectedWikitext);
    }
  }, [htmlToWikitext]);

  // ============================================
  // HANDLE CUT IN VISUAL EDITOR
  // ============================================
  const handleVisualEditorCut = useCallback((e) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    if (range.collapsed) return; // Nothing selected
    
    // Check if selection is within the visual editor
    if (!visualEditorRef.current?.contains(range.commonAncestorContainer)) return;
    
    // Get the selected HTML
    const fragment = range.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);
    const selectedHtml = tempDiv.innerHTML;
    
    // Convert the selected HTML to wikitext
    const selectedWikitext = htmlToWikitext(selectedHtml);
    
    // Only intercept if the selection contains wiki markup (refs, templates, etc.)
    if (selectedWikitext.includes('<ref') || selectedWikitext.includes('{{') || selectedWikitext.includes('[[')) {
      e.preventDefault();
      
      // Set the clipboard data with the wikitext
      if (e.clipboardData) {
        e.clipboardData.setData('text/plain', selectedWikitext);
        e.clipboardData.setData('text/html', selectedHtml);
      }
      
      // Delete the selected content
      range.deleteContents();
      
      // Update the content state from the modified visual editor
      if (visualEditorRef.current) {
        const newWikitext = htmlToWikitext(visualEditorRef.current.innerHTML);
        setContent(newWikitext);
      }
      
      console.log('Cut wikitext:', selectedWikitext);
    }
  }, [htmlToWikitext]);

  // ============================================
  // SAVE PAGE
  // ============================================
  
  // Perform the actual save to the server
  const performSave = async (saveContent) => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to save");
      return;
    }
    
    setIsSaving(true);
    try {
      if (editingNotes) {
        await axios.put(`${API}/wiki/notes/${encodeURIComponent(title)}?token=${token}`, { content: saveContent });
        toast.success("Notes saved successfully!");
        navigate(`/wiki/${encodeURIComponent(title)}?tab=notes`);
      } else if (sectionIndex !== null) {
        await axios.put(`${API}/wiki/page/${encodeURIComponent(title)}/section/${sectionIndex}?token=${token}`, { content: saveContent });
        toast.success("Section saved successfully!");
        navigate(`/wiki/${encodeURIComponent(title)}`);
      } else {
        if (isNewPage) {
          await axios.post(`${API}/wiki/page?token=${token}`, { title: decodeURIComponent(title), content: saveContent });
          toast.success("Page created successfully!");
        } else {
          await axios.put(`${API}/wiki/page/${encodeURIComponent(title)}?token=${token}`, { content: saveContent });
          toast.success("Page saved successfully!");
        }
        navigate(`/wiki/${encodeURIComponent(title)}`);
      }
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSave = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      toast.error("Please log in to save");
      return;
    }

    // Use the originally fetched content for comparison (not the edited content)
    const originalContent = originalFetchedContent;
    
    let saveContent = content;
    if (editorMode === 'visual' && visualEditorRef.current) {
      saveContent = htmlToWikitext(visualEditorRef.current.innerHTML);
    }
    
    // CRITICAL SAFETY CHECK: Never save content with internal placeholders
    // These should never appear in saved wikicode
    const placeholderPattern = /__(?:PRESERVED|REF|SELFREF|TEMPLATE|EXTLINK|NOWIKI)_\d+__/g;
    if (placeholderPattern.test(saveContent)) {
      toast.error("Save aborted: Internal placeholders detected. Please switch to Source Editor and try again.", {
        duration: 8000
      });
      console.error("Placeholder leak detected in save content:", saveContent.match(placeholderPattern));
      return;
    }
    
    // ============================================
    // SMART REF HANDLING: When a full ref definition is removed but self-closing
    // refs with the same name still exist, promote the first self-closing ref
    // to contain the full definition.
    // ============================================
    
    // Extract all named full refs from original: <ref name="xxx">content</ref>
    const fullRefPattern = /<ref\s+name=["']?([^"'\s>]+)["']?\s*>([\s\S]*?)<\/ref>/gi;
    
    const originalFullRefs = {};
    let fullMatch;
    while ((fullMatch = fullRefPattern.exec(originalContent)) !== null) {
      const name = fullMatch[1];
      const refContent = fullMatch[2];
      if (!originalFullRefs[name]) {
        originalFullRefs[name] = { fullTag: fullMatch[0], content: refContent };
      }
    }
    
    // Check which named refs are missing in the new content
    const newFullRefs = {};
    fullRefPattern.lastIndex = 0; // Reset regex
    while ((fullMatch = fullRefPattern.exec(saveContent)) !== null) {
      newFullRefs[fullMatch[1]] = true;
    }
    
    // For each named ref that was removed, check if self-closing refs exist
    for (const refName of Object.keys(originalFullRefs)) {
      if (!newFullRefs[refName]) {
        // This named ref definition was removed - check for self-closing refs
        const selfClosingPattern = new RegExp(
          `<ref\\s+name=["']?${refName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']?\\s*/>`,
          'i'
        );
        
        if (selfClosingPattern.test(saveContent)) {
          // Found a self-closing ref with this name - promote it to full definition
          const { content: storedContent } = originalFullRefs[refName];
          const fullDef = `<ref name="${refName}">${storedContent}</ref>`;
          saveContent = saveContent.replace(selfClosingPattern, fullDef);
          console.log(`Promoted self-closing ref "${refName}" to full definition`);
        }
      }
    }
    
    // Always save - no content loss validation
    performSave(saveContent);
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
    navigate("/wiki/login");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem("wiki_token");
    if (!token) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/wiki/upload?token=${token}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Image "${response.data.filename}" uploaded!`);
      setUploadedImages(prev => [...prev, response.data]);
      setContent(prev => prev + "\n" + response.data.wikicode);
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const insertImageWikicode = (filename) => {
    setContent(prev => prev + `\n[[File:${filename}]]`);
    toast.success("Image inserted");
  };

  // Insert helpers for source editor
  const insertAtCursor = (before, after = "") => {
    const textarea = document.querySelector('[data-testid="wiki-content-textarea"]');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    const newContent = content.substring(0, start) + before + selected + after + content.substring(end);
    setContent(newContent);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  const insertBold = () => insertAtCursor("'''", "'''");
  const insertItalic = () => insertAtCursor("''", "''");
  const insertStrike = () => insertAtCursor("<s>", "</s>");
  const insertHeading = () => insertAtCursor("\n== ", " ==\n");
  const insertList = () => insertAtCursor("\n* ");
  const insertSourceLink = () => {
    const pageName = prompt("Enter page name to link to:");
    if (pageName) insertAtCursor(`[[${pageName}|`, "]]");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <WikiHeader username="" onLogout={() => {}} showSearch={false} />
        <div className="max-w-[800px] mx-auto mt-12 px-4">
          <div className="bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-2xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">Access Restricted</h1>
            <p className="text-[#202122]">Please <Link to="/wiki/login" className="text-[#3366cc] hover:underline">log in</Link> to edit pages.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={username} onLogout={handleLogout} showSearch={true} />

      {/* Article/Notes Tabs */}
      <div className="bg-[#f6f6f6] border-b border-[#a2a9b1]">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex -mb-px">
            <Link to={`/wiki/${encodeURIComponent(title)}/edit`}
              className={`py-2 px-4 text-sm border border-b-0 rounded-t transition-colors ${!editingNotes ? 'bg-white text-[#202122] border-[#a2a9b1] font-medium' : 'bg-[#eaecf0] text-[#3366cc] border-transparent hover:bg-[#e1e3e5]'}`}>
              Article
            </Link>
            <Link to={`/wiki/${encodeURIComponent(title)}/edit?tab=notes`}
              className={`py-2 px-4 text-sm border border-b-0 rounded-t ml-1 transition-colors ${editingNotes ? 'bg-white text-[#202122] border-[#a2a9b1] font-medium' : 'bg-[#eaecf0] text-[#3366cc] border-transparent hover:bg-[#e1e3e5]'}`}>
              Notes
            </Link>
          </div>
        </div>
      </div>

      {/* Action Tabs */}
      <div className="bg-white border-b border-[#a2a9b1]">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex gap-4 -mb-px">
            <Link to={editingNotes ? `/wiki/${encodeURIComponent(title)}?tab=notes` : `/wiki/${encodeURIComponent(title)}`}
              className="py-2 px-4 text-sm text-[#3366cc] hover:underline">Read</Link>
            <span className="py-2 px-4 text-sm border-b-2 border-[#3366cc] text-[#202122] font-medium">Edit</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          <WikiSidebar username={username} />

          {/* Main Content */}
          <main className="flex-1 bg-white border border-[#a2a9b1] p-6">
            <h1 className="font-wiki text-2xl text-[#202122] mb-4 border-b border-[#a2a9b1] pb-3">
              {editingNotes 
                ? `Editing Notes: ${decodeURIComponent(title)}` 
                : sectionIndex !== null 
                  ? `Editing section${sectionHeading ? `: ${sectionHeading}` : ` ${sectionIndex}`} of ${decodeURIComponent(title)}`
                  : `${isNewPage ? "Creating" : "Editing"}: ${decodeURIComponent(title)}`}
            </h1>
            
            {/* Image Upload - available for both articles and notes */}
            <div className="bg-[#f8f9fa] border border-[#a2a9b1] p-4 mb-4">
              <h3 className="font-bold text-[#202122] mb-3">Upload Image</h3>
              <div className="flex items-center gap-4">
                <input type="file" ref={fileInputRef} onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  className="text-sm" disabled={isUploading} />
                {isUploading && <span className="text-sm text-[#72777d]">Uploading...</span>}
              </div>
              {uploadedImages.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-bold text-[#202122] mb-2">Uploaded Images:</h4>
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((img, idx) => (
                      <button key={idx} onClick={() => insertImageWikicode(img.filename)}
                        className="text-xs bg-white border border-[#a2a9b1] px-2 py-1 hover:bg-[#eaf3ff] text-[#3366cc]">
                        {img.filename}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Editor Mode Tabs */}
            <div className="flex border-b border-[#a2a9b1] mb-0">
              <button onClick={switchToVisual}
                className={`px-4 py-2 text-sm font-medium ${editorMode === "visual" ? "text-[#202122] border-b-2 border-[#3366cc] bg-white" : "text-[#72777d] hover:text-[#202122]"}`}>
                Visual Editor
              </button>
              <button onClick={switchToSource}
                className={`px-4 py-2 text-sm font-medium ${editorMode === "source" ? "text-[#202122] border-b-2 border-[#3366cc] bg-white" : "text-[#72777d] hover:text-[#202122]"}`}>
                Source Editor
              </button>
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-1 p-2 bg-[#f8f9fa] border border-t-0 border-[#a2a9b1] flex-wrap">
              {editorMode === "visual" ? (
                <>
                  <button onClick={formatBold} className="p-2 hover:bg-[#eaecf0] rounded" title="Bold">
                    <Bold className="w-4 h-4" />
                  </button>
                  <button onClick={formatItalic} className="p-2 hover:bg-[#eaecf0] rounded" title="Italic">
                    <Italic className="w-4 h-4" />
                  </button>
                  <button onClick={formatStrikethrough} className="p-2 hover:bg-[#eaecf0] rounded" title="Strikethrough">
                    <Strikethrough className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-[#a2a9b1] mx-1" />
                  <button onClick={formatHeading} className="p-2 hover:bg-[#eaecf0] rounded" title="Heading">
                    <Heading2 className="w-4 h-4" />
                  </button>
                  <button onClick={formatList} className="p-2 hover:bg-[#eaecf0] rounded" title="Bullet List">
                    <List className="w-4 h-4" />
                  </button>
                  <button onClick={insertLink} className="p-2 hover:bg-[#eaecf0] rounded" title="Insert Link">
                    <Link2 className="w-4 h-4" />
                  </button>
                  <button onClick={openInsertCitation} className="p-2 hover:bg-[#eaecf0] rounded" title="Insert Citation">
                    <BookMarked className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-[#a2a9b1] mx-1" />
                  <span className="text-xs text-[#72777d] px-2">Click [1] citations to edit • Infobox values are directly editable</span>
                </>
              ) : (
                <>
                  <button onClick={insertBold} className="p-2 hover:bg-[#eaecf0] rounded" title="Bold ('''text''')">
                    <Bold className="w-4 h-4" />
                  </button>
                  <button onClick={insertItalic} className="p-2 hover:bg-[#eaecf0] rounded" title="Italic (''text'')">
                    <Italic className="w-4 h-4" />
                  </button>
                  <button onClick={insertStrike} className="p-2 hover:bg-[#eaecf0] rounded" title="Strikethrough (<s>text</s>)">
                    <Strikethrough className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-[#a2a9b1] mx-1" />
                  <button onClick={insertHeading} className="p-2 hover:bg-[#eaecf0] rounded" title="Heading (== text ==)">
                    <Heading2 className="w-4 h-4" />
                  </button>
                  <button onClick={insertList} className="p-2 hover:bg-[#eaecf0] rounded" title="Bullet List (* item)">
                    <List className="w-4 h-4" />
                  </button>
                  <button onClick={insertSourceLink} className="p-2 hover:bg-[#eaecf0] rounded" title="Link ([[page|text]])">
                    <Link2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            {/* Editor */}
            {editorMode === "visual" ? (
              <div
                key={`visual-editor-${title}`}
                ref={visualEditorRef}
                contentEditable
                onClick={handleVisualEditorClick}
                onKeyDown={handleVisualEditorKeyDown}
                onPaste={handleVisualEditorPaste}
                onCopy={handleVisualEditorCopy}
                onCut={handleVisualEditorCut}
                onInput={handleVisualEditorInput}
                className="wiki-visual-editor w-full min-h-[48rem] border border-t-0 border-[#a2a9b1] p-4 focus:outline-none overflow-auto"
                suppressContentEditableWarning
              />
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full min-h-[48rem] border border-t-0 border-[#a2a9b1] p-3 font-mono text-sm focus:border-[#3366cc] focus:outline-none"
                placeholder={editingNotes ? "Enter notes here..." : "Enter wikitext here..."}
                data-testid="wiki-content-textarea"
              />
            )}
            
            <div className="mt-4 flex gap-4">
              <button onClick={handleSave} disabled={isSaving}
                data-testid="wiki-save-button"
                className="bg-[#3366cc] text-white py-2 px-6 text-sm font-medium hover:bg-[#2a4b8d] disabled:opacity-50">
                {isSaving ? "Saving..." : (editingNotes ? "Save notes" : (isNewPage ? "Create page" : "Save changes"))}
              </button>
              <Link to={editingNotes ? `/wiki/${encodeURIComponent(title)}?tab=notes` : `/wiki/${encodeURIComponent(title)}`}
                className="py-2 px-6 text-sm text-[#3366cc] hover:underline">Cancel</Link>
            </div>
          </main>
        </div>
      </div>

      {/* Edit Reference Modal */}
      <EditModal
        isOpen={editingRef !== null}
        onClose={() => { setEditingRef(null); setRefContent(''); }}
        title="Edit Citation"
      >
        {editingRef?.selfClosing ? (
          <div className="text-[#72777d]">
            <p className="mb-2">This is a repeated citation that references another citation by name.</p>
            <p className="font-mono bg-[#f8f9fa] p-2 rounded">{editingRef?.full}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#72777d] mb-3">Edit the citation content (wikitext format):</p>
            <textarea
              value={refContent}
              onChange={(e) => setRefContent(e.target.value)}
              className="w-full h-48 border border-[#a2a9b1] p-3 font-mono text-sm rounded focus:border-[#3366cc] focus:outline-none"
              placeholder="Citation content..."
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setEditingRef(null); setRefContent(''); }}
                className="px-4 py-2 text-sm text-[#72777d] hover:text-[#202122]">
                Cancel
              </button>
              <button onClick={saveRefEdit}
                className="px-4 py-2 bg-[#3366cc] text-white text-sm rounded hover:bg-[#2a4b8d]">
                Save Citation
              </button>
            </div>
          </>
        )}
      </EditModal>

      {/* Edit Template Modal */}
      <EditModal
        isOpen={editingTemplate !== null}
        onClose={() => { setEditingTemplate(null); setTemplateContent(''); }}
        title={`Edit Template: ${editingTemplate?.name || ''}`}
      >
        <p className="text-sm text-[#72777d] mb-3">Edit the template (wikitext format):</p>
        <textarea
          value={templateContent}
          onChange={(e) => setTemplateContent(e.target.value)}
          className="w-full h-64 border border-[#a2a9b1] p-3 font-mono text-sm rounded focus:border-[#3366cc] focus:outline-none"
          placeholder="Template content..."
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => { setEditingTemplate(null); setTemplateContent(''); }}
            className="px-4 py-2 text-sm text-[#72777d] hover:text-[#202122]">
            Cancel
          </button>
          <button onClick={saveTemplateEdit}
            className="px-4 py-2 bg-[#3366cc] text-white text-sm rounded hover:bg-[#2a4b8d]">
            Save Template
          </button>
        </div>
      </EditModal>

      {/* Insert Citation Modal */}
      <EditModal
        isOpen={showInsertCitation}
        onClose={() => { setShowInsertCitation(false); setInsertCitationCode(''); }}
        title="Insert Citation"
      >
        <p className="text-sm text-[#72777d] mb-3">
          Paste your citation wikicode below. It will be inserted before the references section.
        </p>
        <p className="text-xs text-[#72777d] mb-3 bg-[#f8f9fa] p-2 rounded">
          Example: <code className="text-[#202122]">{`<ref>{{cite web|title=Example|url=https://example.com}}</ref>`}</code>
        </p>
        <textarea
          value={insertCitationCode}
          onChange={(e) => setInsertCitationCode(e.target.value)}
          className="w-full h-48 border border-[#a2a9b1] p-3 font-mono text-sm rounded focus:border-[#3366cc] focus:outline-none"
          placeholder="<ref>{{cite web|title=...|url=...}}</ref>"
          autoFocus
        />
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => { setShowInsertCitation(false); setInsertCitationCode(''); }}
            className="px-4 py-2 text-sm text-[#72777d] hover:text-[#202122]">
            Cancel
          </button>
          <button onClick={handleInsertCitation}
            className="px-4 py-2 bg-[#3366cc] text-white text-sm rounded hover:bg-[#2a4b8d]">
            Insert Citation
          </button>
        </div>
      </EditModal>

      <style>{`
        .wiki-visual-editor {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #202122;
        }
        .wiki-visual-editor:focus {
          outline: none;
        }
        .wiki-visual-editor h2 {
          font-size: 1.5rem;
          font-weight: normal;
          border-bottom: 1px solid #a2a9b1;
          padding-bottom: 0.25rem;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .wiki-visual-editor h3 {
          font-size: 1.17rem;
          font-weight: bold;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .wiki-visual-editor h4 {
          font-size: 1rem;
          font-weight: bold;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .wiki-visual-editor p {
          margin-bottom: 0.75rem;
        }
        .wiki-visual-editor a.wiki-link {
          color: #3366cc;
          text-decoration: none;
        }
        .wiki-visual-editor a.wiki-link:hover {
          text-decoration: underline;
        }
        .wiki-visual-editor a.external-link {
          color: #3366cc;
        }
        .wiki-visual-editor ul {
          margin-left: 1.5rem;
          list-style-type: disc;
          margin-bottom: 0.75rem;
        }
        .wiki-visual-editor li {
          margin-bottom: 0.25rem;
        }
        .wiki-visual-editor strong, .wiki-visual-editor b {
          font-weight: bold;
        }
        .wiki-visual-editor em, .wiki-visual-editor i {
          font-style: italic;
        }
        .wiki-visual-editor s, .wiki-visual-editor strike, .wiki-visual-editor del {
          text-decoration: line-through;
        }
        .wiki-visual-editor u, .wiki-visual-editor ins {
          text-decoration: underline;
        }
        .wiki-visual-editor small {
          font-size: 0.85em;
        }
        .wiki-visual-editor sub {
          vertical-align: sub;
          font-size: 0.85em;
        }
        .wiki-visual-editor sup {
          vertical-align: super;
          font-size: 0.85em;
        }
        .wiki-visual-editor mark {
          background-color: #ffff00;
          padding: 0 2px;
        }
        
        /* Collapse box - Wikipedia style */
        .wiki-collapse-box {
          border: 1px solid #a2a9b1;
          margin: 1rem 0;
          background-color: #f8f9fa;
          border-radius: 4px;
          overflow: hidden;
        }
        .wiki-collapse-box.wiki-collapse-edit {
          border: 2px solid #3366cc;
        }
        .wiki-collapse-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: linear-gradient(to bottom, #f8f9fa, #eaecf0);
          border-bottom: 1px solid #a2a9b1;
          cursor: pointer;
          user-select: none;
        }
        .wiki-collapse-header:hover {
          background: linear-gradient(to bottom, #eaecf0, #dde0e4);
        }
        .wiki-collapse-toggle {
          font-size: 0.8em;
          color: #3366cc;
          transition: transform 0.2s;
        }
        .wiki-collapse-header.expanded .wiki-collapse-toggle {
          transform: rotate(90deg);
        }
        .wiki-collapse-title {
          font-weight: 600;
          color: #202122;
          flex: 1;
        }
        .wiki-collapse-hint {
          font-size: 0.75em;
          color: #3366cc;
        }
        .wiki-collapse-content {
          padding: 12px;
          background-color: #fff;
        }
        .wiki-collapse-content.expanded {
          display: block !important;
        }
        .wiki-collapse-content-editable {
          min-height: 60px;
          outline: none;
        }
        .wiki-collapse-content-editable:focus {
          background-color: #fffef0;
        }
        .wiki-collapse-content-editable:empty::before {
          content: "Type collapsed content here...";
          color: #999;
          font-style: italic;
        }
        
        /* Preserved markup pills (signatures, comments, etc.) */
        .wiki-preserved-pill {
          display: inline-block;
          background: linear-gradient(to bottom, #fff8e1, #ffecb3);
          color: #795548;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 0.8em;
          font-family: monospace;
          border: 1px solid #ffca28;
          user-select: none;
          cursor: default;
        }
        .wiki-preserved-pill.wiki-signature {
          background: linear-gradient(to bottom, #e3f2fd, #bbdefb);
          color: #1565c0;
          border-color: #64b5f6;
        }
        .wiki-preserved-pill.wiki-comment {
          background: linear-gradient(to bottom, #f3e5f5, #e1bee7);
          color: #7b1fa2;
          border-color: #ba68c8;
        }
        .wiki-preserved-pill.wiki-nowiki {
          background: linear-gradient(to bottom, #fff3e0, #ffe0b2);
          color: #e65100;
          border-color: #ffb74d;
        }
        .wiki-pre {
          background-color: #f8f9fa;
          border: 1px solid #a2a9b1;
          padding: 12px;
          font-family: monospace;
          font-size: 0.9em;
          overflow-x: auto;
          white-space: pre-wrap;
          margin: 0.5rem 0;
        }
        .wiki-code {
          background-color: #f8f9fa;
          border: 1px solid #ddd;
          padding: 2px 4px;
          font-family: monospace;
          font-size: 0.9em;
          border-radius: 3px;
        }
        
        /* Ref pills - clickable citation markers */
        .wiki-ref-pill {
          display: inline-block;
          background: linear-gradient(to bottom, #eaf3ff, #d5e3f7);
          color: #3366cc;
          padding: 0 4px;
          border-radius: 3px;
          font-size: 0.75em;
          font-weight: 600;
          vertical-align: super;
          cursor: pointer;
          border: 1px solid #a7d7f9;
          user-select: all;
          transition: all 0.15s ease;
        }
        .wiki-ref-pill:hover {
          background: linear-gradient(to bottom, #d5e3f7, #c0d4eb);
          border-color: #72a8d5;
        }
        .wiki-ref-pill.wiki-ref-repeat {
          background: linear-gradient(to bottom, #f0f0f0, #e0e0e0);
          color: #666;
          border-color: #ccc;
        }
        /* Better selection visibility for citation pills */
        .wiki-ref-pill::selection,
        .wiki-ref-pill *::selection {
          background-color: #3366cc !important;
          color: white !important;
        }
        .wiki-visual-editor ::selection {
          background-color: #b4d5fe;
        }
        /* When citation is within selection, highlight it distinctly */
        .wiki-ref-pill.selected,
        .wiki-ref-pill:focus {
          background: #3366cc !important;
          color: white !important;
          border-color: #1a4d99 !important;
          outline: 2px solid #ffcc00;
          outline-offset: 1px;
        }
        
        /* Template pills */
        .wiki-template-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(to bottom, #f8f9fa, #eaecf0);
          border: 1px solid #a2a9b1;
          border-radius: 4px;
          padding: 4px 10px;
          font-size: 0.875em;
          cursor: pointer;
          user-select: all;
          transition: all 0.15s ease;
        }
        .wiki-template-pill:hover {
          background: linear-gradient(to bottom, #eaecf0, #dde0e4);
          border-color: #72777d;
        }
        .wiki-template-pill.selected {
          background: #3366cc !important;
          color: white !important;
          border-color: #1a4d99 !important;
          outline: 2px solid #ffcc00;
          outline-offset: 1px;
        }
        
        /* Selection highlight for preserved pills */
        .wiki-preserved-pill.selected {
          background: #3366cc !important;
          color: white !important;
          border-color: #1a4d99 !important;
          outline: 2px solid #ffcc00;
          outline-offset: 1px;
        }
        
        /* Infobox - Wikipedia style */
        .wiki-infobox {
          float: right;
          clear: right;
          margin: 0.5rem 0 1rem 1rem;
          width: 22rem;
          font-size: 0.875rem;
          border: 1px solid #a2a9b1;
          background-color: #f8f9fa;
          border-collapse: collapse;
        }
        .wiki-infobox-title {
          text-align: center;
          font-size: 1.1rem;
          font-weight: bold;
          padding: 0.5rem;
          background-color: #eaecf0;
          border: 1px solid #a2a9b1;
        }
        .wiki-infobox-label {
          text-align: left;
          padding: 0.35rem 0.5rem;
          vertical-align: top;
          font-weight: normal;
          background-color: #eaecf0;
          border: 1px solid #a2a9b1;
          width: 40%;
        }
        .wiki-infobox-value {
          padding: 0.35rem 0.5rem;
          vertical-align: top;
          border: 1px solid #a2a9b1;
          background-color: #fff;
          cursor: text;
        }
        .wiki-infobox-value:focus {
          outline: 2px solid #3366cc;
          outline-offset: -2px;
          background-color: #fefefe;
        }
        .wiki-infobox-value:hover {
          background-color: #f8f9fa;
        }
        .wiki-infobox a.wiki-link {
          color: #3366cc;
          text-decoration: none;
        }
        
        /* Reflist - Wikipedia style */
        .wiki-reflist {
          margin-top: 2rem;
          padding: 1rem;
          background-color: #f8f9fa;
          border: 1px solid #a2a9b1;
          border-radius: 4px;
          clear: both;
        }
        .wiki-reflist-heading {
          font-size: 1.25rem;
          font-weight: normal;
          border-bottom: 1px solid #a2a9b1;
          padding-bottom: 0.25rem;
          margin-bottom: 0.75rem;
        }
        .wiki-references {
          font-size: 0.9rem;
          margin-left: 2rem;
          line-height: 1.5;
        }
        .wiki-reference-item {
          margin-bottom: 0.5rem;
          word-wrap: break-word;
        }
        .wiki-ref-backlink {
          color: #3366cc;
          font-size: 0.8em;
          margin-right: 0.5rem;
        }
        .wiki-ref-content {
          color: #202122;
        }
        .wiki-ref-content a {
          color: #3366cc;
          text-decoration: none;
        }
        .wiki-ref-content a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
